"use client";

import { useEffect, useState } from "react";
import { MenuIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useSidebar } from "@/components/ui/sidebar";
import { useIsMobile } from "@/hooks/use-mobile";
import { usePlayer } from "@/contexts/player-context";

interface MobileMenuButtonProps {
  ariaLabel?: string;
  className?: string;
  iconClassName?: string;
}

export function MobileMenuButton({
  ariaLabel = "Open menu",
  className,
  iconClassName,
}: MobileMenuButtonProps) {
  // 安全地获取 sidebar context，避免 SSR 错误
  let toggleSidebar: () => void;
  try {
    const sidebarContext = useSidebar();
    toggleSidebar = sidebarContext.toggleSidebar;
  } catch {
    toggleSidebar = () => {};
  }

  const isMobile = useIsMobile();
  let isFullPlayerOpen = false;

  try {
    const player = usePlayer();
    isFullPlayerOpen = Boolean(player.isFullPlayerOpen);
  } catch {
    isFullPlayerOpen = false;
  }

  const [isVideoFullscreen, setIsVideoFullscreen] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    const updateState = (active: boolean) => setIsVideoFullscreen(active);

    const handleFullscreenChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ active: boolean }>;
      updateState(Boolean(customEvent.detail?.active));
    };

    window.addEventListener("nf-video-fullscreen-change", handleFullscreenChange);
    updateState(document.body.classList.contains("nf-video-fullscreen"));

    return () => {
      window.removeEventListener("nf-video-fullscreen-change", handleFullscreenChange);
    };
  }, []);

  if (!isMobile || isFullPlayerOpen || isVideoFullscreen) {
    return null;
  }

  return (
    <Button
      size="icon"
      variant="ghost"
      onClick={toggleSidebar}
      className={cn(
        "pointer-events-auto fixed top-1 right-3 md:top-[2px] z-[70] h-8 sm:h-9 w-12 rounded-xl border border-border/60 bg-background/90 shadow-sm backdrop-blur",
        className
      )}
      aria-label={ariaLabel}
    >
      <MenuIcon className={cn("h-6 w-6", iconClassName)} />
    </Button>
  );
}
